import { StandardEvents } from '@shopify/events';
import { sellingPlanValue, planPrice } from './p80-purchase-options-logic.js';

/**
 * One-time vs subscribe purchase options (Bean Hollow product page and featured
 * coffee). Writes the chosen selling plan into the product form's `selling_plan`
 * input (linked with the `form` attribute) so any selling-plan app (Recharge,
 * Shopify Subscriptions) works through Shopify's standard cart API.
 *
 * Prices and plans are server-rendered per variant. When the shopper picks another
 * variant, the block swaps in its freshly rendered markup from the product-select
 * response (as Horizon's product-price does) and keeps the chosen purchase type and
 * delivery frequency.
 *
 * @extends {HTMLElement}
 */
class P80PurchaseOptions extends HTMLElement {
    /** @type {Element | null} */
    #section = null;

    connectedCallback() {
        this.addEventListener('change', this.#update);
        this.#section = this.closest('.shopify-section, dialog');
        this.#section?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect);
        this.#update();
    }

    disconnectedCallback() {
        this.removeEventListener('change', this.#update);
        this.#section?.removeEventListener(StandardEvents.productSelect, this.#handleProductSelect);
    }

    /**
     * Swap in the new variant's prices and plans, keeping the shopper's choices.
     * @param {Event & { promise?: Promise<{ detail?: { html?: Document } }> }} event
     */
    #handleProductSelect = (event) => {
        if (!(event.target instanceof Element) || event.target.closest('product-card')) return;
        event.promise
            ?.then(({ detail } = {}) => {
                const next = detail?.html?.querySelector(
                    `p80-purchase-options[data-block-id="${this.dataset.blockId}"]`
                );
                if (!(next instanceof HTMLElement)) return;
                const type = /** @type {HTMLInputElement | null} */ (
                    this.querySelector('input[type="radio"]:checked')
                )?.value;
                const frequency = /** @type {HTMLSelectElement | null} */ (
                    this.querySelector('select[data-frequency]')
                )?.value;
                this.dataset.planPrices = next.dataset.planPrices || '{}';
                this.replaceChildren(...[...next.childNodes].map((n) => n.cloneNode(true)));
                const radio = /** @type {HTMLInputElement | null} */ (
                    type ? this.querySelector(`input[type="radio"][value="${type}"]`) : null
                );
                if (radio) radio.checked = true;
                const select = /** @type {HTMLSelectElement | null} */ (
                    this.querySelector('select[data-frequency]')
                );
                if (select && frequency && [...select.options].some((o) => o.value === frequency)) {
                    select.value = frequency;
                }
                this.#update();
            })
            .catch(() => {});
    };

    #update = () => {
        const type = /** @type {HTMLInputElement | null} */ (
            this.querySelector('input[type="radio"]:checked')
        );
        const frequency = /** @type {HTMLSelectElement | null} */ (
            this.querySelector('select[data-frequency]')
        );
        const input = /** @type {HTMLInputElement | null} */ (
            this.querySelector('input[name="selling_plan"]')
        );
        const purchaseType = type?.value === 'subscribe' ? 'subscribe' : 'onetime';

        if (input) {
            input.value = sellingPlanValue(purchaseType, frequency?.value);
            input.disabled = input.value === '';
        }
        if (frequency) frequency.disabled = purchaseType !== 'subscribe';

        const priceEl = this.querySelector('[data-subscribe-price]');
        if (priceEl instanceof HTMLElement && frequency) {
            const prices = JSON.parse(this.dataset.planPrices || '{}');
            priceEl.textContent = planPrice(
                prices,
                frequency.value,
                priceEl.dataset.fallback || priceEl.textContent || ''
            );
        }

        this.toggleAttribute('data-subscribe', purchaseType === 'subscribe');
    };
}

if (!customElements.get('p80-purchase-options')) {
    customElements.define('p80-purchase-options', P80PurchaseOptions);
}
